import { Alert, Card, CardContent, CardHeader, Grid, Skeleton } from '@mui/material';
import { AxiosError } from 'axios';
import { useEffect, useMemo, useState } from 'react';
import {
  Currency,
  convertAbelCurrency,
  formatThoundsandNumber,
  stableHash,
  stableHashUnit,
} from '../../../../abelcommon';
import AnalyticsRoundedIcon from '@mui/icons-material/AnalyticsRounded';
import './poolSummaryStyle.css';
import { loadBalancerAPI, PoolSummaryInfo } from '../../../../api/load-balancer';
import { UsersDialog } from './UsersDialog';
import { MiningAccountsChartDialog } from './MiningAccountsChartDialog';
import { AbelMinedByBlockRangesDialog } from './AbelMinedByBlockRangesDialog';
import { AllHashRateChartDialog } from './AllHashRateDialog';
import { AllGPUClientsOnlineDialog } from './AllGPUClientsOnlineDialog';
import { AllGPUCardDistributionDialog } from './AllGPUCardDistributionDialog';

export const AllPoolSummary = ({
  poolSummaryInfo,
  poolSummaryInfoLoading,
  poolSummaryInfoError,
}: {
  poolSummaryInfo: PoolSummaryInfo;
  poolSummaryInfoLoading: boolean;
  poolSummaryInfoError: AxiosError;
}) => {
  const [milisecTimeCounter, setMilisecTimeCounter] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [modalContent, setModalContent] = useState<React.ReactNode | null>(null);

  const handleClickOpen = (content: React.ReactNode) => {
    setModalContent(content);
    setOpenModal(true);
  };

  const handleClose = () => {
    setModalContent(null);
    setOpenModal(false);
  };

  useEffect(() => {
    if (poolSummaryInfoLoading || !poolSummaryInfo) return;

    setMilisecTimeCounter(poolSummaryInfo.payoutOverview.nextDistribution.nextDistributionTimeInSecond * 1000);
  }, [poolSummaryInfo, poolSummaryInfoLoading]);

  const {
    data: totalMinedByRanges,
    isLoading: totalMinedsLoading,
    error: totalMinedsError,
  } = loadBalancerAPI.hookWrapperQuery.useGetAbelMinedByBlockRangesChart('all');

  const nextDistributionTime = useMemo(() => {
    if (milisecTimeCounter <= 0) {
      return {
        hours: '00',
        minutes: '00',
        seconds: '00',
      };
    }

    const diffInHours = Math.floor(milisecTimeCounter / (1000 * 60 * 60));
    const diffInMinutes = Math.floor((milisecTimeCounter / (1000 * 60)) % 60);
    const diffInSecond = Math.floor((milisecTimeCounter / 1000) % 60);

    return {
      hours: diffInHours === 0 ? '00' : `${diffInHours < 10 ? '0' : ''}${diffInHours}`,
      minutes: diffInMinutes === 0 ? '00' : `${diffInMinutes < 10 ? '0' : ''}${diffInMinutes}`,
      seconds: diffInSecond === 0 ? '00' : `${diffInSecond < 10 ? '0' : ''}${diffInSecond}`,
    };
  }, [milisecTimeCounter]);

  const summaryInfo = useMemo(() => {
    const propsPlaceholder = [
      { key: 'Users', value: '', icon: '', modalContent: '' },
      { key: 'Mining Accounts', value: '', icon: '', modalContent: '' },
      { key: 'Hash Rate', value: '', icon: '', modalContent: '' },
      { key: 'Pool Fee', value: '', icon: '', modalContent: '' },
      { key: 'ABELS Mined by Block Range', value: '', icon: '', modalContent: '' },
      { key: 'GPU Cards Distribution', value: '', icon: '', modalContent: '' },
      { key: 'Mining Rigs Online', value: '', icon: '', modalContent: '' },
      { key: 'Next Payout', value: '', icon: '', subtitle: '', modalContent: '' },
    ];

    return !poolSummaryInfo
      ? propsPlaceholder
      : [
          {
            key: 'Users',
            value: formatThoundsandNumber(poolSummaryInfo.users),
            icon: <AnalyticsRoundedIcon style={{ fontSize: '30px' }} />,
            modalContent: 'Users',
          },
          {
            key: 'Mining Accounts',
            value: formatThoundsandNumber(poolSummaryInfo.miners),
            icon: <AnalyticsRoundedIcon style={{ fontSize: '30px' }} />,
            modalContent: 'Mining Accounts',
          },
          {
            key: 'Hash Rate',
            value: `${formatThoundsandNumber(stableHash(poolSummaryInfo.hashrate * 1000), 4)} ${stableHashUnit(poolSummaryInfo.hashrate * 1000)}/s`,
            icon: <AnalyticsRoundedIcon style={{ fontSize: '30px' }} />,
            modalContent: 'Hash Rate',
          },
          { key: 'Pool Fee', value: `${formatThoundsandNumber(poolSummaryInfo.poolFee)}%`, icon: '', modalContent: '' },
          {
            key: 'ABELS Mined by Block Range',
            subtitle:
              !totalMinedsError &&
              !totalMinedsLoading &&
              `[${totalMinedByRanges[totalMinedByRanges.length - 1].startHeight} - ${totalMinedByRanges[totalMinedByRanges.length - 1].endHeight}]`,
            value: `${parseFloat(
              convertAbelCurrency(poolSummaryInfo.totalReward, Currency.Neutrinos, Currency.Abel).toFixed(2)
            )} ABEL `,
            icon: <AnalyticsRoundedIcon style={{ fontSize: '30px' }} />,
            modalContent: 'ABELS Mined by Block Range',
          },
          {
            key: 'GPU Cards Distribution',
            value: formatThoundsandNumber(poolSummaryInfo.totalCardCount),
            icon: <AnalyticsRoundedIcon style={{ fontSize: '30px' }} />,
            modalContent: 'GPU Cards Distribution',
          },
          {
            key: 'Mining Rigs Online',
            value: formatThoundsandNumber(poolSummaryInfo.totalMachineCount),
            icon: <AnalyticsRoundedIcon style={{ fontSize: '30px' }} />,
            modalContent: 'Mining Rigs Online',
          },
          {
            key: 'Next Payout',
            value: `@Block ${!poolSummaryInfo.payoutOverview ? 0 : poolSummaryInfo.payoutOverview.nextDistribution.currentBlockHeight}`,
            subtitle: `In ${nextDistributionTime.hours}h ${nextDistributionTime.minutes}m ${nextDistributionTime.seconds}s`,
            icon: '',
            modalContent: 'Next Payout',
          },
        ];
  }, [
    poolSummaryInfo,
    totalMinedsError,
    totalMinedsLoading,
    totalMinedByRanges,
    nextDistributionTime.hours,
    nextDistributionTime.minutes,
    nextDistributionTime.seconds,
  ]);
  return (
    <Card>
      <CardHeader title="Maxpool Summary" />
      <CardContent sx={{ overflow: 'auto' }}>
        {poolSummaryInfoError ? (
          <Alert severity="error" sx={{ mb: 1 }}>
            {poolSummaryInfoError?.message}
          </Alert>
        ) : (
          <Grid container spacing={1}>
            {summaryInfo.map(({ key, value, icon, subtitle, modalContent }) => (
              <Grid item xs={6} sm={4} md={4} lg={3} xl={3} key={key}>
                <InfoBox
                  subtitle={subtitle}
                  icon={icon}
                  title={value}
                  content={key}
                  isLoading={poolSummaryInfoLoading}
                  onClick={() => handleClickOpen(modalContent)}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </CardContent>

      {modalContent === 'Users' && openModal === true && <UsersDialog onClose={handleClose} />}

      {modalContent === 'ABELS Mined by Block Range' && openModal === true && (
        <AbelMinedByBlockRangesDialog onClose={handleClose} />
      )}
      {modalContent === 'GPU Cards Distribution' && openModal === true && (
        <AllGPUCardDistributionDialog open={true} onClose={handleClose} />
      )}
      {modalContent === 'Mining Rigs Online' && openModal === true && (
        <AllGPUClientsOnlineDialog open={true} onClose={handleClose} />
      )}
      {modalContent === 'Mining Accounts' && openModal === true && (
        <MiningAccountsChartDialog open={true} onClose={handleClose} />
      )}

      {modalContent === 'Hash Rate' && openModal === true && (
        <AllHashRateChartDialog open={modalContent === 'Hash Rate' && openModal === true} onClose={handleClose} />
      )}
    </Card>
  );
};

interface InfoBoxProps {
  title: string;
  content: string;
  isLoading: boolean;
  icon: React.ReactNode;
  subtitle: string;
  onClick?: () => void;
}

const InfoBox = ({ title, content, isLoading, subtitle, icon, onClick }: InfoBoxProps) => {
  return isLoading ? (
    <Skeleton variant="rectangular" sx={{ minHeight: '82px', borderRadius: 1 }} />
  ) : (
    <div className="global-item-all-pool">
      <div className="content-global">{content}</div>
      {subtitle ? (
        <div className="title-icon-subtitle-global">
          <div className="title-icon-global">
            <span>{title}</span>
            <span className="icon" onClick={onClick}>
              {icon}
            </span>
          </div>
          <span style={{ fontSize: 'small', fontWeight: '600' }}>{subtitle}</span>
        </div>
      ) : (
        <div className="title-icon-global">
          <span>{title}</span>
          <span className="icon" onClick={onClick}>
            {icon}
          </span>
        </div>
      )}
    </div>
  );
};
